import type { IntegrationStatus } from '../../../../src/application/sources/integrations.js';
import type { QuickBooksStatus } from '../../../../src/application/sources/quickbooks.js';
import type { NetSuiteStatus } from '../../../../src/application/sources/netsuite-status.js';
import type { SourceSyncSummary } from '../../../../src/application/sources/sync.js';
import type { SlackInstallation } from '../../../../src/connectors/slack/membership.js';
import type { MailboxAuth } from '../../../../src/application/gmail/mailbox-auth.js';
import type { MailboxHealth as MailboxSendingHealth } from '../../../../src/application/gmail/mailbox-sending.js';
export type { MailboxSendingHealth };

/**
 * State and plain-language wording for the Integrations page. Kept free of React so every status and sentence the
 * page shows is unit-tested. Nothing here ever contains record contents or secret values.
 */
export type SourceName = 'sequence' | 'rillet';
export type Schedule = { intervalMinutes: number; scheduled: boolean; lastAt: string | null; lastStatus: string | null;
  nextAt: string | null };
export type MailCheck = { intervalSeconds: number; lastCheckedAt: string | null };
export type IntegrationsState = { live: boolean; canSave: boolean; connectors: Record<SourceName, IntegrationStatus>;
  last: SourceSyncSummary | null; schedule?: Schedule; mail?: MailCheck; quickbooks?: QuickBooksStatus; netsuite?: NetSuiteStatus;
  sourceOfRecord?: 'auto' | 'sequence' | 'quickbooks'; webhooks?: Webhooks; mailboxAuth?: MailboxAuth | null;
  /** Owners only: one health entry per connected sending mailbox (volume against cap, bounce rate). */
  mailboxHealth?: MailboxSendingHealth[];
  /** Shadow mode: approvals are recorded and nothing is emailed (added 2026-10-30). */
  shadowMode?: boolean;
  /** Who approves routine email under the active policy (added 2026-10-30). */
  sendMode?: 'approve' | 'autonomous' | 'mixed';
  /** Outbound calls: off, test (rings one number) or live (added 2026-10-30). */
  voiceMode?: 'off' | 'test' | 'live' };
/** The Sequence webhook: whether its signing secret is saved (••••last4), the URL to register and the events to pick. */
export type Webhooks = { sequence: { configured: boolean; secret: string | null; url: string; events: string[] } };
export type GmailState = { configured: boolean; accounts: { email: string; connectedAt: string }[] };
export type SlackState = { configured: boolean; installation: SlackInstallation | null; botName?: string | null };

/** connected: working. attention: set up but something needs a person. off: not set up. demo: synthetic data. */
export type Tone = 'connected' | 'attention' | 'off' | 'demo';
export interface Health { tone: Tone; label: string; detail: string | null }

export const toneLabel: Record<Tone, string> = {
  connected: 'Connected', attention: 'Needs attention', off: 'Not connected', demo: 'Demo stand-in',
};
const health = (tone: Tone, detail: string | null = null): Health => ({ tone, label: toneLabel[tone], detail });
export const vendorName: Record<SourceName, string> = { sequence: 'Sequence', rillet: 'Rillet' };

/** "3 min ago", "in 2 min", "just now": a time relative to now, in whole minutes, hours or days. */
export function relativeTime(at: string, now = Date.now()) {
  const minutes = Math.round((new Date(at).getTime() - now) / 60_000);
  const size = Math.abs(minutes);
  if (size < 1) return 'just now';
  const [value, unit] = size < 60 ? [size, 'min'] : size < 2880 ? [Math.round(size / 60), 'h'] : [Math.round(size / 1440), 'd'];
  return minutes < 0 ? `${value} ${unit} ago` : `in ${value} ${unit}`;
}

/** "Syncs automatically every 5 min" or, on hosts without a schedule, "Syncs when you choose Sync now". */
export function scheduleLine(schedule: Schedule | undefined, now = Date.now()) {
  if (!schedule?.scheduled) return 'Syncs when you choose Sync now';
  const next = schedule.nextAt ? `, next ${relativeTime(schedule.nextAt, now)}` : '';
  return `Syncs automatically every ${schedule.intervalMinutes} min${next}`;
}

const plural = (count: number, word: string) => `${count.toLocaleString('en-US')} ${word}${count === 1 ? '' : 's'}`;

/** "511 invoices, 54 customers · synced 3 min ago". Counts only. */
export function syncLine(last: SourceSyncSummary | null, now = Date.now()) {
  if (!last) return 'No sync has run yet';
  const when = `synced ${relativeTime(last.finishedAt, now)}`;
  if (last.status === 'failed') return `Last sync failed · ${relativeTime(last.finishedAt, now)}`;
  if (last.status === 'not_configured') return 'Nothing to sync until Rillet, Sequence or QuickBooks is connected';
  const counts = `${plural(last.fetched.invoices, 'invoice')}, ${plural(last.fetched.customers, 'customer')}`;
  return `${counts} · ${when}${last.status === 'partial' ? ' (partial)' : ''}`;
}

/** The detail row for one source: how its part of the last sync went. */
export function sourceRunLine(name: SourceName | 'quickbooks', last: SourceSyncSummary | null, now = Date.now()) {
  const run = last?.sources.find(item => item.source === name);
  if (!last || !run) return null;
  const when = relativeTime(last.finishedAt, now);
  if (run.complete && !run.errors.length) return `Read in full ${when}`;
  const problems = run.errors.length + run.rejected;
  return `Partly read ${when}${problems ? `, ${plural(problems, 'issue')}` : ''}`;
}

export function sourceHealth(name: SourceName, state: IntegrationsState): Health {
  const status = state.connectors[name];
  if (!state.live) return health('demo', `This host uses demo data. ${vendorName[name]} connects on a live host.`);
  if (status.source === 'error') return health('attention', 'The saved credentials can no longer be read. Enter them again.');
  if (status.source === 'none') {
    return health('off', name === 'rillet' ? 'Add your Rillet API key so Alder can read open AR and cleared payments.'
      : 'Add your Sequence client id and secret so Alder can read invoices and pay links.');
  }
  const run = state.last?.sources.find(item => item.source === name);
  if (run && (!run.complete || run.errors.length)) {
    return health('attention', `The last sync could not read all of ${vendorName[name]}. Choose Sync now; if it repeats, `
      + 'update the credentials.');
  }
  return health('connected');
}

/** Whether a sync reads QuickBooks, by SOURCE_OF_RECORD and which other books sources are set up (recordClients). */
export function quickbooksIsRecord(state: IntegrationsState) {
  const others = (['sequence', 'rillet'] as const).some(name => state.connectors[name].source !== 'none');
  return state.sourceOfRecord === 'quickbooks' || (state.sourceOfRecord !== 'sequence' && !others);
}

export function quickbooksHealth(state: IntegrationsState, outcome: string | null = null): Health {
  const status = state.quickbooks;
  if (!state.live) return health('demo', 'This host uses demo data. QuickBooks connects on a live host.');
  if (!status || status.source === 'none') return health('off', 'Add your Intuit app’s client id and secret, then connect a company.');
  if (status.source === 'error' || status.connection === 'error') {
    return health('attention', 'The saved QuickBooks values can no longer be read. Enter them again and reconnect.');
  }
  if (!status.connection) {
    return health(outcome === 'failed' ? 'attention' : 'off', outcome === 'failed'
      ? 'QuickBooks was not connected. Choose Connect QuickBooks and approve access in Intuit.'
      : 'Choose Connect QuickBooks and pick the company Alder should read.');
  }
  if (!quickbooksIsRecord(state)) {
    return health('attention', 'Connected, but not read: Sequence or Rillet is the record. Set SOURCE_OF_RECORD=quickbooks on the host.');
  }
  const run = state.last?.sources.find(item => item.source === 'quickbooks');
  if (run && (!run.complete || run.errors.length)) {
    const auth = run.errors.some(error => error.kind === 'auth');
    return health('attention', auth ? 'QuickBooks refused the connection. Reconnect QuickBooks.'
      : 'The last sync could not read all of QuickBooks. Choose Sync now; if it repeats, reconnect.');
  }
  return health('connected');
}

/**
 * NetSuite arrives by push (Ampersand reads it and posts to Alder), so its status is about deliveries, not syncs. The first
 * delivery pins the account. Until then the card says what is missing: Alder's webhook on this host, or the admin's connection.
 */
export function netsuiteHealth(status: NetSuiteStatus): Health {
  if (status.account) return health('connected');
  if (!status.webhookConfigured) {
    return health('off', 'Alder finishes the NetSuite link on this host first. Your NetSuite admin can prepare the view-only role now.');
  }
  return health('off', 'Ready for your NetSuite admin: set up the view-only role below. The first delivery links your account.');
}

/** What the customer's NetSuite admin does once (docs/connectors/NETSUITE.md, "Customer's NetSuite admin"). */
export const netsuiteAdminSteps = [
  'Setup → Company → Enable Features → SuiteCloud: turn on REST Web Services and OAuth 2.0.',
  'Create a role with view-only permissions for customers and invoices, and nothing else. This is what keeps Alder read-only.',
  'Create an integration record with only the Client Credentials (machine to machine) grant, and copy its Client ID.',
  'Generate an RSA key pair and register the certificate under OAuth 2.0 Client Credentials (M2M) Setup, against the view-only role.',
  'Note your NetSuite account id. Alder connects with you on a short call, where you enter these values yourself; never email them.',
] as const;

/** What the client's Microsoft 365 admin does so Alder can use one Outlook mailbox. */
export const outlookAdminSteps = [
  'Create or choose the shared mailbox Alder sends from, for example ar@yourcompany.com.',
  'In the Microsoft Entra admin center, approve the Alder app for your organisation when we send the consent link.',
  'Limit Alder to that one mailbox with an application access policy, so it cannot open any other mailbox.',
  'Add the sending mailbox to your SPF record and turn on DKIM for the domain, so reminders land in the inbox.',
] as const;

export function gmailHealth(state: GmailState): Health {
  if (state.accounts.length) return health('connected');
  if (!state.configured) return health('off', 'Gmail needs the host’s Google sign-in to be set up first.');
  return health('off', 'Connect the mailbox Alder sends from. You approve both permissions in Google.');
}

export function slackHealth(state: SlackState): Health {
  const installation = state.installation;
  if (!installation) {
    return health('off', state.configured ? 'Add Alder to Slack, then invite it to your private AR channel.'
      : 'Slack needs the host’s Slack app to be set up first.');
  }
  if (!installation.channel_id) {
    const bot = state.botName || 'Alder';
    return health('attention', `Choose the AR channel. Create a private channel and type /invite @${bot}.`);
  }
  return health(installation.mode === 'mock' ? 'demo' : 'connected');
}

/** "4 of 4 connected · last sync 2 min ago". Demo connectors count as connected: they are working, on demo data. */
export function healthSummary(items: Health[], last: SourceSyncSummary | null, now = Date.now()) {
  const working = items.filter(item => item.tone === 'connected' || item.tone === 'demo').length;
  const attention = items.filter(item => item.tone === 'attention').length;
  const parts = [`${working} of ${items.length} connected`];
  if (attention) parts.push(`${attention} need${attention === 1 ? 's' : ''} attention`);
  parts.push(last ? `last sync ${relativeTime(last.finishedAt, now)}` : 'no sync yet');
  return parts.join(' · ');
}

/** "Checks for replies every minute · last checked 2 min ago". */
export function mailCheckLine(mail: MailCheck | undefined, now = Date.now()) {
  if (!mail) return null;
  const every = mail.intervalSeconds === 60 ? 'every minute'
    : mail.intervalSeconds < 60 ? `every ${mail.intervalSeconds} s` : `every ${Math.round(mail.intervalSeconds / 60)} min`;
  const last = mail.lastCheckedAt ? ` · last checked ${relativeTime(mail.lastCheckedAt, now)}` : '';
  return `Checks for replies ${every}${last}`;
}

/** "3 minutes", "1 hour", "2 days": how old something is, for sentences like "Data is 3 minutes old". */
export function ageWords(at: string, now = Date.now()) {
  const minutes = Math.max(0, Math.round((now - new Date(at).getTime()) / 60_000));
  if (minutes < 1) return 'less than a minute';
  const [value, unit] = minutes < 60 ? [minutes, 'minute'] : minutes < 2880 ? [Math.round(minutes / 60), 'hour']
    : [Math.round(minutes / 1440), 'day'];
  return `${value} ${unit}${value === 1 ? '' : 's'}`;
}

/** Data counts as stale after three missed checks, and never sooner than 45 minutes. */
export const staleAfterMinutes = (intervalMinutes: number) => Math.max(45, intervalMinutes * 3);

/** How fresh the books are, in words, and why it matters when they are not. Null on demo hosts. */
export interface Freshness { text: string; stale: boolean; reason: string | null }
export function booksFreshness(state: IntegrationsState, now = Date.now()): Freshness | null {
  if (!state.live) return null;
  const schedule = state.schedule; const last = state.last;
  const every = schedule?.scheduled ? `checks every ${schedule.intervalMinutes} min` : 'syncs when you choose Sync now';
  if (!last || last.status === 'not_configured') return { text: `Not read yet · ${every}`, stale: false, reason: null };
  if (last.status === 'failed') {
    return { text: `Last read failed ${relativeTime(last.finishedAt, now)} · ${every}`, stale: true,
      reason: 'The last read failed, so Alder holds sending on stale balances. Choose Sync now; if it repeats, check the connection.' };
  }
  const age = (now - new Date(last.finishedAt).getTime()) / 60_000;
  const stale = Boolean(schedule?.scheduled) && age > staleAfterMinutes(schedule!.intervalMinutes);
  return { text: `Updated ${relativeTime(last.finishedAt, now)} · ${every}`, stale,
    reason: stale ? `No read for ${ageWords(last.finishedAt, now)}, so Alder holds sending on stale balances. Choose Sync now.` : null };
}

/**
 * NetSuite arrives by push, and only records that changed are sent, so a quiet day is normal: age alone never means
 * stale. Copy names NetSuite only; the delivery service is plumbing the customer never sees.
 */
export function netsuiteFreshness(lastDeliveryAt: string | null, now = Date.now()): Freshness {
  if (!lastDeliveryAt) return { text: 'Waiting for the first records from NetSuite', stale: false, reason: null };
  return { text: `Last change received ${relativeTime(lastDeliveryAt, now)} · changes arrive within about 10 min`, stale: false, reason: null };
}

/** A working connection whose data went stale needs a person; the stale reason becomes its next step. */
export function withFreshness(base: Health, freshness: Freshness | null): Health {
  if (!freshness?.stale || base.tone !== 'connected') return base;
  return { tone: 'attention', label: toneLabel.attention, detail: freshness.reason };
}

export type BooksName = 'netsuite' | 'quickbooks' | 'sequence' | 'rillet';
export const booksLabel: Record<BooksName, string> = { netsuite: 'NetSuite', quickbooks: 'QuickBooks', sequence: 'Sequence', rillet: 'Rillet' };

/** Which books this instance uses. Unused ones move to "Other books you can connect" and don't count against the summary. */
export function booksInUse(state: IntegrationsState): BooksName[] {
  const used: BooksName[] = [];
  if (state.netsuite?.account) used.push('netsuite');
  if (state.quickbooks && state.quickbooks.source !== 'none' && (state.quickbooks.connection || state.quickbooks.source === 'error')) used.push('quickbooks');
  for (const name of ['sequence', 'rillet'] as const) if (state.connectors[name].source !== 'none') used.push(name);
  return used;
}

/** "Alder reads invoices from Sequence." Mirrors the sync's record choice (Rillet over Sequence, SOURCE_OF_RECORD for QuickBooks). */
export function recordLine(state: IntegrationsState): string {
  if (!state.live) return 'This host uses demo books, so nothing is read from your systems.';
  const used = booksInUse(state);
  if (state.netsuite?.account) return 'Alder reads invoices and customers from NetSuite.';
  if (used.includes('quickbooks') && quickbooksIsRecord(state)) return 'Alder reads invoices and payments from QuickBooks.';
  const rillet = used.includes('rillet'); const sequence = used.includes('sequence');
  if (rillet && sequence && state.sourceOfRecord !== 'sequence') {
    return 'Alder reads balances from Rillet and invoices, contacts and pay links from Sequence.';
  }
  if (sequence) return 'Alder reads invoices from Sequence.';
  if (rillet) return 'Alder reads invoices and payments from Rillet.';
  return 'Connect your books so Alder can read your invoices.';
}

/** The honest line under a "Sandbox copy" badge. Production gets no badge and no line. */
export function sandboxLine(vendor: string, shadow: boolean) {
  return shadow
    ? `Shadow mode reads a sandbox copy of your ${vendor} data, so figures can lag your live account. Switch to production at cutover.`
    : `Alder is reading a ${vendor} sandbox account, not your live books.`;
}

/** The page's one-line health: what needs attention first, else that everything works and how old the data is. */
export function pageSummary(items: Health[], freshestAt: string | null, now = Date.now()) {
  const attention = items.filter(item => item.tone === 'attention').length;
  if (attention) return `${attention} thing${attention === 1 ? ' needs' : 's need'} attention.`;
  const off = items.filter(item => item.tone === 'off').length;
  const age = freshestAt ? ` Data is ${ageWords(freshestAt, now)} old.` : '';
  if (off) return `${items.length - off} of ${items.length} connected.${age}`;
  return `Everything is connected.${age}`;
}

/** The mailbox reassurance, true to who approves under the active policy. */
export function sendingLine(mode: IntegrationsState['sendMode'], shadow = false) {
  if (shadow) return 'Shadow mode: Alder records each approved email and sends nothing to customers.';
  if (mode === 'autonomous') return 'Alder sends routine emails within your policy; replies and exceptions come to a person.';
  if (mode === 'mixed') return 'Alder sends routine emails within your policy; the rest wait for a person to approve.';
  return 'Alder sends only emails a person approved.';
}

export function voiceHealth(mode: IntegrationsState['voiceMode']): Health {
  if (mode === 'live') return health('connected');
  if (mode === 'test') return { tone: 'demo', label: 'Test mode', detail: 'Calls ring only your test number, never a customer.' };
  return health('off', 'Calls are off. Turn them on to let Alder call customers who have not answered email.');
}

/** Reply checks: stale after five missed checks and never sooner than 15 minutes. */
export function mailFreshness(mail: MailCheck | undefined, now = Date.now()): Freshness | null {
  if (!mail) return null;
  const every = mail.intervalSeconds === 60 ? 'every minute'
    : mail.intervalSeconds < 60 ? `every ${mail.intervalSeconds} s` : `every ${Math.round(mail.intervalSeconds / 60)} min`;
  if (!mail.lastCheckedAt) return { text: `Replies not checked yet · checks ${every}`, stale: false, reason: null };
  const age = (now - new Date(mail.lastCheckedAt).getTime()) / 1000;
  const stale = age > Math.max(900, mail.intervalSeconds * 5);
  return { text: `Replies checked ${relativeTime(mail.lastCheckedAt, now)} · checks ${every}`, stale,
    reason: stale ? `Alder has not checked for replies in ${ageWords(mail.lastCheckedAt, now)}. Reconnect Gmail if this continues.` : null };
}
